import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  output,
} from '@angular/core';
import { getNews } from '@fc-store/selectors';
import { Store } from '@ngrx/store';
import { setShowNews } from '@fc-store/actions';
import {
  getStoredNewsIds,
  storeNewsIds,
} from '@fc-core/services/news-storage.utils';
import { ButtonComponent } from '@fc-shared/ui/buttons/button.component';
import { NgTagComponent } from '@fc-shared/ui/tags/ng-tag.component';
import { EmptyStateComponent } from '@fc-shared/ui/empty-state/empty-state.component';
import { MomentFormatPipe } from '@fc-shared/pipes/moment-format-pipe/moment-format.pipe';
import { ImageViewComponent } from '@fc-shared/ui/image-loader/image-view.component';

@Component({
  selector: 'fc-news-tab',
  host: {
    class: 'flex h-full min-h-0 flex-col',
  },
  template: `
    <div
      class="flex items-center justify-between border-b border-secondary-100 px-20 py-12"
    >
      <div class="text-lg leading-30 font-medium text-grey-900">News</div>
      <fc-button variant="icon" (click)="closePopup.emit()" icon="icon-close" />
    </div>

    <div class="flex min-h-0 flex-1 flex-col gap-24 overflow-y-auto p-20">
      @for (
        newsItem of news();
        track newsItem.id;
        let isFirstNewsItem = $first
      ) {
        <a
          [href]="newsItem.meta.html_url"
          target="_blank"
          rel="noopener noreferrer"
          class="group flex flex-col gap-16 no-underline"
        >
          <div class="flex flex-col items-center gap-8">
            <div class="flex items-center gap-8">
              @if (isNew(newsItem.id)) {
                <fc-ng-tag color="primary">New</fc-ng-tag>
              }
              <fc-ng-tag color="secondary">
                {{
                  newsItem.meta.first_published_at
                    | momentFormat: 'MMMM D, YYYY'
                }}
              </fc-ng-tag>
            </div>

            <div class="flex w-full flex-col gap-4 text-center">
              <div
                class="text-lg leading-30 font-medium text-grey-900 transition-colors duration-200 group-hover:text-primary-600"
              >
                {{ newsItem.title }}
              </div>
              <p class="line-clamp-3 text-sm leading-20 text-grey-600">
                {{ newsItem.description }}
              </p>
              <span class="text-sm leading-20 font-medium text-primary-600">
                Read more
              </span>
            </div>
          </div>

          @if (
            newsItem.preview_image?.meta?.download_url ||
              newsItem.image?.meta?.download_url;
            as newsImageUrl
          ) {
            <div class="h-280 overflow-hidden rounded-lg">
              <fc-image-view
                class="transition-transform duration-200 group-hover:scale-105"
                fill
                shape="rectangle"
                [priority]="isFirstNewsItem"
                [image]="newsImageUrl"
                alt="News preview image"
              />
            </div>
          }
        </a>
      } @empty {
        <fc-empty-state
          class="m-auto"
          icon="icon-news-outline"
          title="There is no news here yet"
          subtitle="We will keep you updated on new features, updates, and important changes to Fleet Chaser. Stay tuned for our updates!"
        />
      }
    </div>
  `,
  imports: [
    ButtonComponent,
    NgTagComponent,
    EmptyStateComponent,
    MomentFormatPipe,
    ImageViewComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewsTabComponent implements AfterViewInit {
  private store = inject(Store);
  readonly news = this.store.selectSignal(getNews);
  readonly seenNewsIds = new Set(getStoredNewsIds());
  closePopup = output();

  ngAfterViewInit() {
    this.store.dispatch(setShowNews({ showNewsState: 0 }));
    storeNewsIds(this.news()?.map((item) => item.id) ?? []);
  }

  isNew(newsId: number): boolean {
    return !this.seenNewsIds.has(newsId);
  }
}
